import React, { forwardRef } from 'react';
import { Box, FormControl, InputLabel, MenuItem, Stack } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import { MenuIconName, MenuType, MenuSectionUiModuleBody } from '../../../../../API';
import { useLocales, useTheme } from '../../../../../hooks';
import { FormBody, FormField } from '../../styles';
import { UIModuleTypeForm } from '../../UIModuleForm';
import { PagesAutocomplete } from '../PagesAutocomplete';
import TextValidator from '../../../../shared/TextValidator';
import { NONE } from '../../../../../utils/types/genericTypes';
import { LokaliseAutocomplete } from '../../../../shared/LokaliseAutocomplete';
import { CustomIcon } from '../../../../shared/CustomIcon';
import { menuIconPaths, menuIconViewbox } from '../../../../../utils/menuIcons';
import Select from '../../../../shared/Select';
import { menuSectionFormTestIds } from '../../../../shared/TestsIds';

const MenuSectionForm = forwardRef<UIModuleTypeForm<MenuSectionUiModuleBody>>(() => {
  const { control } = useFormContext<MenuSectionUiModuleBody>();
  const { t } = useLocales();
  const { formControlColor } = useTheme();

  return (
    <FormBody data-testid={menuSectionFormTestIds.formBody}>
      <FormField>
        <Controller
          control={control}
          name="titleLokaliseKey"
          render={({ field: { value, name, onChange } }) => (
            <LokaliseAutocomplete
              label={t('general.title')}
              name={name}
              value={value as string}
              data-testid={menuSectionFormTestIds.keySelector}
              onChange={onChange}
            />
          )}
        />
      </FormField>
      <FormField>
        <FormControl color={formControlColor} required>
          <Controller
            control={control}
            name="targetId"
            render={({ field: { value, onChange } }) => (
              <PagesAutocomplete
                value={value}
                onChange={onChange}
                disableClearable={false}
                renderInput={(params) => (
                  <TextValidator
                    value={value}
                    {...params}
                    name="targetId"
                    label={`${t('layouts.target')} (${t('general.optional')})`}
                    color={formControlColor}
                  />
                )}
              />
            )}
          />
        </FormControl>
      </FormField>
      <FormField>
        <FormControl color={formControlColor} required>
          <InputLabel id="iconNameSelector">{t('layouts.menu_item_icon_name')}</InputLabel>
          <Controller
            control={control}
            name="iconName"
            render={({ field: { value, onChange } }) => (
              <Select
                label={t('layouts.menu_item_icon_name')}
                labelId="iconNameSelector"
                value={value || NONE}
                onChange={onChange}
              >
                <MenuItem value={NONE}>
                  <em>{t('general.none')}</em>
                </MenuItem>
                {Object.values(MenuIconName).map((name) => (
                  <MenuItem key={name} value={name}>
                    <Stack direction="row" gap={3}>
                      <Box sx={{ width: 22, display: 'flex' }}>
                        <CustomIcon pathString={menuIconPaths[name] as string} viewBox={menuIconViewbox} />
                      </Box>
                      {name}
                    </Stack>
                  </MenuItem>
                ))}
              </Select>
            )}
          />
        </FormControl>
      </FormField>
      <FormField>
        <FormControl color={formControlColor}>
          <InputLabel id="pageSelector">{t('layouts.menu_type')}</InputLabel>
          <Controller
            control={control}
            name="type"
            render={({ field: { value, onChange } }) => (
              <Select label={t('layouts.menu_type')} labelId="pageSelector" value={value || NONE} onChange={onChange}>
                <MenuItem value={NONE}>
                  <em>{t('general.none')}</em>
                </MenuItem>
                {Object.values(MenuType).map((name) => (
                  <MenuItem key={name} value={name}>
                    {name}
                  </MenuItem>
                ))}
              </Select>
            )}
          />
        </FormControl>
      </FormField>
    </FormBody>
  );
});

MenuSectionForm.displayName = 'MenuSectionForm';

export default MenuSectionForm;
